import { isSnapshot } from '../multiplayer/protocol';
import { createElement, setText } from './dom';
import { createIcon } from './icons';
import { setLabel } from './i18n';

/** Presence belongs to the live city, including visitors outside the local interest radius. */
export function createOnlineVisitors(button: HTMLButtonElement): () => void {
  const { counter, value, label } = presenceCounter(button);
  let active: AbortController | null = null;
  let disposed = false;

  const refresh = async (): Promise<void> => {
    if (disposed || document.hidden || active) return;
    const request = new AbortController();
    active = request;
    const timeout = window.setTimeout(() => {
      request.abort();
    }, 4000);
    try {
      const response = await fetch('/api/worlds/amsterdam1538/state/', {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: request.signal,
      });
      if (!response.ok) throw new Error('Presence unavailable');
      const snapshot: unknown = await response.json();
      if (!isSnapshot(snapshot) || !Number.isSafeInteger(snapshot.count) || snapshot.count < 0)
        throw new Error('Invalid city presence');
      if (disposed || request.signal.aborted) return;
      setText(value, String(snapshot.count));
      setText(label, ' visitors online');
      setLabel(counter, 'title', 'Visitors online · refreshes every 5 seconds');
    } catch {
      if (disposed || document.hidden) return;
      setText(value, '—');
      setText(label, ' visitor count unavailable');
      setLabel(counter, 'title', 'Live visitor count is unavailable · retries every 5 seconds');
    } finally {
      window.clearTimeout(timeout);
      if (active === request) active = null;
    }
  };
  const visibility = (): void => {
    if (document.hidden) active?.abort();
    else void refresh();
  };
  const interval = window.setInterval(() => void refresh(), 5000);
  document.addEventListener('visibilitychange', visibility);
  void refresh();
  return () => {
    disposed = true;
    active?.abort();
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', visibility);
  };
}

function presenceCounter(button: HTMLButtonElement) {
  const counter = createElement('span', 'online-visitors', button);
  counter.id = 'online-visitors';
  counter.setAttribute('role', 'status');
  counter.setAttribute('aria-live', 'polite');
  counter.setAttribute('aria-atomic', 'true');
  const icon = createIcon('visitors', 16);
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('stroke-width', '1.5');
  counter.append(icon);
  const value = createElement('span', 'online-visitors-count', counter);
  const label = createElement('span', 'sr-only', counter);
  setText(value, '—');
  setText(label, ' visitors online');
  button.setAttribute(
    'aria-describedby',
    `${button.getAttribute('aria-describedby') ?? ''} ${counter.id}`.trim()
  );
  return { counter, value, label };
}
